import React, { useState } from "react";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { Badge, Button, CodeView, StackTrace } from "@/components";
import classNames from "classnames";
import { Issue as IssueType } from "@/types";
import { useAuth, useSve } from "@/contexts";
import Skeleton from "react-loading-skeleton";
import { priorityToString } from "@/utils";
import BlurImg from "@/assets/issue-blur-back.png";
import { useNavigate } from "react-router-dom";
import "./style.scss";
import { DismissSelect } from "./DismissSelect";
interface Props {
  data: IssueType;
  isLoading?: boolean;
  onDismiss(
    category: string,
    index: number,
    dismissReason: string,
    comment: string,
  ): Promise<void>;
  onUndismiss(category: string, index: number): void;
}
export const Issue = ({
  data,
  isLoading = false,
  onDismiss,
  onUndismiss,
}: Props) => {
  const [isClosed, setIsOpen] = useState(false);
  const { sveData } = useSve();
  const { userInfo } = useAuth();
  let navigate = useNavigate();
  const handleUpgrade = () => {
    navigate(`/account#plan`);
  };

  const handleUndismissIssue = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onUndismiss(data.type, data.index - 1);
  };

  return (
    <div className="issue">
      {isLoading ? (
        <>
          <Skeleton width={160} height={24} />
          <Skeleton count={10} containerClassName="mt-2" />
        </>
      ) : (
        <>
          <div className="issue-header" onClick={() => setIsOpen(!isClosed)}>
            <div className="issue-title">{`${data.type} No.${data.index}`}</div>

            {data.ignore ? (
              <div className="ms-auto issue-dismiss-badge d-flex flex-column">
                <Badge
                  color="gray"
                  label="Dismissed"
                  className="dismissed my-auto"
                />
                <Badge
                  color="gray"
                  label="Reopen"
                  className="reopen my-auto"
                  onClick={handleUndismissIssue}
                />
              </div>
            ) : (
              <DismissSelect
                issue={data}
                className="ms-auto issue-dismiss"
                dismissReasons={[
                  {
                    title: "Won't Fix",
                    id: "WontFix",
                    description: "This issue is not relevant",
                  },
                  {
                    title: "False Positive",
                    id: "FalsePositive",
                    description: "This issue is not valid",
                  },
                  {
                    title: "Used in Test",
                    id: "Test",
                    description: "This issue is not in production code",
                  },
                ]}
                onDismiss={onDismiss}
              />
            )}
            <Badge
              color={
                priorityToString(data.priority) === "Critical"
                  ? "dark-red"
                  : priorityToString(data.priority) === "High"
                    ? "red"
                    : priorityToString(data.priority) === "Medium"
                      ? "yellow"
                      : priorityToString(data.priority) === "Low"
                        ? "light-yellow"
                        : "gray"
              }
              label={priorityToString(data.priority)}
              className="ms-3 my-auto"
            />

            {isClosed ? (
              <FaCaretDown className="ms-2 my-auto" />
            ) : (
              <FaCaretUp className="ms-2 my-auto" />
            )}
          </div>
          <div className={classNames("issue-content", { isClosed })}>
            {data.hide &&
            (userInfo?.plan.name === "Free" ||
              userInfo?.plan.name === "Basic") ? (
              <div className="blur-back">
                <img src={BlurImg} alt="blur" />
                <Button
                  className="blur-back-button"
                  color="light-purple"
                  onClick={handleUpgrade}
                  icon="star"
                >
                  Upgrade plan to view this issue
                </Button>
              </div>
            ) : (
              <>
                <div className="mt-4 text-bold">{data.description}</div>
                {data.inst && (
                  <>
                    <p className="issue-line mt-3">
                      {data.inst?.filename}:{data.inst?.line}
                    </p>
                    <CodeView data={data?.inst?.snippet || ""} />
                    {!!data.inst?.stacktrace.length && (
                      <StackTrace
                        className="mt-3"
                        data={data.inst.stacktrace}
                      />
                    )}
                  </>
                )}
                {data.inst1 && (
                  <>
                    <p className="issue-line mt-3">
                      {data.inst1?.filename}:{data.inst1?.line}
                    </p>
                    <CodeView data={data?.inst1?.snippet || ""} />
                    {!!data.inst1?.stacktrace.length && (
                      <StackTrace
                        className="mt-3"
                        data={data.inst1.stacktrace}
                      />
                    )}
                  </>
                )}
                {data.inst2 && (
                  <>
                    <p className="issue-line mt-3">
                      {data.inst2?.filename}:{data.inst2?.line}
                    </p>
                    <CodeView data={data?.inst2?.snippet || ""} />
                    {!!data.inst2?.stacktrace.length && (
                      <StackTrace
                        className="mt-3"
                        data={data.inst2.stacktrace}
                      />
                    )}
                  </>
                )}
                {sveData[data.id] && (
                  <div className="d-flex mt-4">
                    <a
                      href={sveData[data.id].url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button icon="info">Detailed explanation</Button>
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
